import React, { ReactElement, useEffect, useState } from "react";
import classNames from "classnames";
import {
    FormFieldReplacementInput, useGetDynamicOptionsQuery, FormFieldOption, FieldTypedValueInput,
    FormFieldOptionSettings, FormFieldType,
} from "../../../__generated__/api";
import EditableControl, { ControlType, RecursiveOption, ValueTypes } from "../../editable-text/EditableText";
import { useTheme } from "../../../contexts/Theme";

export interface DynamicPickerProps<TIsRequired=false> {
    placeholder?: string;
    value: ValueTypes;
    parentValues?: ValueTypes[];
    dynamicChoiceContext: string;
    optionsSettings?: FormFieldOptionSettings;
    integrationId: string;
    isFormField: boolean | undefined;
    type: ControlType;
    isRequired: TIsRequired | undefined;
    labels: string[] | undefined;
    onChange?: (
        newString: ValueTypes | (TIsRequired extends false ? undefined : never),
        replacements?: FormFieldReplacementInput[],
        displayText?: string,
        dynamicChoice?: string
    ) => void;
    onFirstLoad?: (
        displayText?: string,
        dynamicChoice?: string
    ) => void;

    getCurrentForm?: () => FieldTypedValueInput[];
}

const FORCE_RERENDER_TYPES = new Set([
    FormFieldType.AsyncDropdown,
    FormFieldType.Dropdown,
    FormFieldType.SearchText,
    FormFieldType.MultiLevelDropdown,
    FormFieldType.MultiSelectDropdown,
]);

function DynamicPicker<TIsRequired extends boolean = false>({
    onChange, onFirstLoad, placeholder, value, type, isRequired, parentValues,
    dynamicChoiceContext, integrationId, labels, getCurrentForm, optionsSettings,
    isFormField,
}: DynamicPickerProps<TIsRequired>): ReactElement {
    const [options, setOptions] = useState<RecursiveOption<FormFieldOption>[]>([]);
    const currentValues = (getCurrentForm?.() ?? []).filter(formField => FORCE_RERENDER_TYPES.has(formField.type));
    const theme = useTheme();
    const { loading: isLoading, data } = useGetDynamicOptionsQuery({
        variables: {
            filters: {
                context: dynamicChoiceContext,
                integrationId,
                value: parentValues as string[],
                currentValues,
            },
        },
    });
    const [isFirstLoad, setIsFirstLoad] = useState(true);
    useEffect(() => {
        setIsFirstLoad(true);
    }, []);

    useEffect(() => {
        if (!data?.workflowServiceChoiceValues?.options) {
            return;
        }
        setOptions(data?.workflowServiceChoiceValues?.options.map(row => ({
            label: row.label,
            value: row.value as string,
            data: row,
            children: row.children?.map(child => ({
                label: child.label,
                value: child.value as string,
                data: child,
            })),
        })) ?? []);

        if (isFirstLoad) {
            const optionValue = Array.isArray(value) ? value.firstElement : value;
            const selectedOption = data?.workflowServiceChoiceValues?.options.find(opt => opt.value === optionValue);
            if (selectedOption) {
                onFirstLoad?.(
                    selectedOption.label,
                    selectedOption.dynamicChoiceContext as string | undefined,
                );
            }
        }
    }, [data?.workflowServiceChoiceValues?.options]);

    if (isLoading && options.isEmpty) {
        return (
            <div>Loading...</div>
        );
    }

    if (options.isEmpty && optionsSettings?.emptyOptionsMessage) {
        return (
            <div className={classNames(
                theme.isV3 ? "" : "text-light",
                "my-2 border rounded p-2",
            )}
            >
                {optionsSettings?.emptyOptionsMessage}
            </div>
        );
    }

    return (
        <EditableControl
            isFormField={isFormField}
            options={options}
            placeholder={placeholder}
            isRequired={isRequired}
            labels={labels}
            type={type}
            isEditing
            value={value}
            integrationId={integrationId}
            onChange={(newValue, replacements, text) => {
                const optionValue = Array.isArray(newValue) ? newValue.firstElement : newValue;
                const selectedOption = options.find(option => option.value === optionValue);
                onChange?.(
                    newValue as Parameters<typeof onChange>[0],
                    replacements,
                    text,
                    selectedOption?.data?.dynamicChoiceContext as string | undefined,
                );
            }}
        />
    );
}

DynamicPicker.defaultProps = {
    placeholder: undefined,
    onChange: undefined,
    onFirstLoad: undefined,
    parentValues: undefined,
    getCurrentForm: undefined,
    optionsSettings: undefined,
};

export default DynamicPicker as <
    TIsRequired extends boolean = false
> (
    props: DynamicPickerProps<TIsRequired>
) => ReturnType<typeof DynamicPicker>;
